import { ApplicationView, useController } from '@aha-app/mvc';
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import { ReactEditor, useSlate, useSlateSelection } from 'slate-react';
import type Tool from '../../tools/Tool';
import type AhaEditor from '../../AhaEditor';
import AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import ToolButton from '../Toolbar/ToolButton';
import styles from './SelectionToolbar.module.css';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import findScrollContainer from '../../util/findScrollContainer';
import { cancelEventHandlersOutsideFocusableElements } from '../../util/mouseHandlers';
import useAhaEditorFocused from '../useAhaEditorFocused';

interface SelectionToolbarProps {
  ahaEditor: AhaEditor;
  tools: (typeof Tool)[];
  position: 'above' | 'right';
}

// Display a toolbar related to the current selection to the right of the editor.
const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  ahaEditor,
  tools,
  position,
}: SelectionToolbarProps) => {
  const controller = useController(AhaEditorV2Controller);
  const selection = useSlateSelection();
  const editor = useSlate();
  const { editorContainer } = controller;
  const editorIsFocused = useAhaEditorFocused();
  const fullScreen = controller.state.pluginState.isFullScreen ?? false;
  // Create the popper that will position the toolbar.
  const [popperElement, setPopperElement] = useState<HTMLElement | null>();

  let scrollContainer: HTMLElement | undefined;
  if (editorContainer) {
    scrollContainer = findScrollContainer(editorContainer) ?? undefined;
  }

  // While the user is actively dragging a selection we want to hide the
  // toolbar. Otherwise when dragging upwards the mouse can go over the toolbar
  // and cause it to flicker. We need to handle the mouse being dragged outside
  // the editor, so we only hide the editor for short time while the mouse is
  // being dragged.
  const selectionTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isSelecting, setIsSelecting] = useState(false);
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (e.buttons === 1) {
      setIsSelecting(true);
      // Reset the timer to hide the toolbar.
      clearTimeout(selectionTimeout.current);
      selectionTimeout.current = setTimeout(() => {
        setIsSelecting(false);
      }, 500);
    } else {
      setIsSelecting(false);
    }
  }, []);
  const handleMouseUp = useCallback(() => {
    setIsSelecting(false);
    clearTimeout(selectionTimeout.current);
  }, []);
  useEffect(() => {
    if (!editorContainer) return;

    editorContainer.addEventListener('mousemove', handleMouseMove);
    editorContainer.addEventListener('mouseup', handleMouseUp);

    return () => {
      editorContainer.removeEventListener('mousemove', handleMouseMove);
      editorContainer.removeEventListener('mouseup', handleMouseUp);
    };
  }, [editorContainer, handleMouseMove, handleMouseUp]);

  const VirtualSelection = useMemo(
    () => ({
      getBoundingClientRect: () => {
        if (!selection || !editorContainer) return new DOMRect();

        const domRange = ReactEditor.toDOMRange(editor, selection);
        if (!domRange) return new DOMRect();

        const clientRects = domRange.getClientRects();
        if (clientRects.length === 0 && !clientRects[0]) return new DOMRect();

        const firstRect = clientRects[0];

        // When displaying to the right, make it look like the selection takes
        // up the whole height of the editor.
        if (position === 'right') {
          const boundingElement = editorContainer?.querySelector(
            '.aha-editor-v2__content'
          );
          if (!boundingElement) return new DOMRect();

          const boundingRect = boundingElement.getBoundingClientRect();
          const styles = window.getComputedStyle(boundingElement);
          const borderRight = parseFloat(styles.borderRightWidth) || 0;

          firstRect.x = boundingRect.right - borderRight;
          firstRect.width = 1;
        }

        return firstRect;
      },
      contextElement: editorContainer as Element,
    }),
    [editor, selection]
  );

  // Move the offset from the edge of the screen in full screen mode
  // We need to `useMemo`, otherwise the toolbar re-renders forever
  const toolbarOffset = useMemo<[number, number]>(
    () => (fullScreen ? [0, -50] : [0, 15]),
    [fullScreen]
  );

  const {
    styles: popperStyles,
    attributes,
    forceUpdate,
  } = usePopper(VirtualSelection, popperElement, {
    // We want the toolbar to be positioned above the element always, but
    // overlap if necessary so it is always visible during scrolling.
    placement: position === 'above' ? 'top' : 'right',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          tether: true,
          altAxis: true,
          boundary: scrollContainer,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: [],
        },
      },
      {
        name: 'offset',
        options: {
          offset: toolbarOffset,
        },
      },
    ],
  });

  // Trigger re-renders when the editor changes size.
  useEffect(() => {
    if (!editorContainer || !forceUpdate) return;

    const boundingElement = editorContainer?.querySelector(
      '.aha-editor-v2__content'
    );
    if (!boundingElement) return;

    const resizeObserver = new ResizeObserver(forceUpdate);
    resizeObserver.observe(boundingElement);
    return () => resizeObserver.disconnect(); // clean up
  }, [editorContainer, forceUpdate]);

  const popperRefCallback = useCallback(
    (el: HTMLDivElement) => {
      setPopperElement(el);

      if (el?.isConnected && el?.showPopover) {
        el.showPopover();
      }
    },
    [setPopperElement]
  );

  if (isSelecting) return null;
  if (controller.hasTableSelection()) return null;
  if (controller.state.readOnly) return null;
  if (!editorIsFocused) return null;

  const toolButtons = tools.reduce<React.JSX.Element[]>((toolButtons, tool) => {
    if (tool.available({ editor, controller })) {
      toolButtons.push(
        <ToolButton
          key={tool.toolName}
          editor={editor}
          controller={controller}
          tool={tool}
        />
      );
    }
    return toolButtons;
  }, []);

  if (!toolButtons.length) return null;

  return ReactDOM.createPortal(
    <>
      <div
        ref={popperRefCallback}
        popover="manual"
        style={popperStyles.popper}
        {...attributes.popper}
        {...cancelEventHandlersOutsideFocusableElements}
        className={classNames(styles.selectionToolbar, {
          [styles.selectionToolbarRight]: position === 'right',
          [styles.selectionToolbarAbove]: position === 'above',
        })}
        data-testid="selection-toolbar"
      >
        {toolButtons}
      </div>
    </>,
    controller.toolbarContainer
  );
};

export default ApplicationView(SelectionToolbar);
